/**
 * CONCEPT 15 — THE PRIMARY MARK. One geometry definition, three consumers.
 *
 * This file is plain .mjs on purpose. The React component imports it, the generated
 * brand SVGs are written from it, and `scripts/generate-favicons.mjs` (Node, no TS
 * transform) imports it too. That is the whole point: the last time this brand had a
 * mark, a build script owned favicon/apple-touch/OG and quietly kept serving the OLD
 * logo, because the geometry lived in three places and only two got updated. Hand-editing
 * a generated file is a no-op the next build erases. So there is exactly one source here.
 *
 * PROVENANCE — these are MEASURED, not eyeballed. Every number below was read off the
 * delivered `logo-mark-15.png` (1800x1800) by sampling pixel runs per colour class, then
 * translated into a tight viewBox by subtracting the art's origin (144, 373). If the
 * owner ships a real vector, diff against this before replacing it.
 *
 * THE DECISION THIS FILE ENCODES (2026-09-05, owner). The delivered brand board titles
 * this composition "CONCEPT 15 / PRIMARY LOGO" and states: "The #15 horizon and
 * commercial building ARE the logo. The wordmark and operational devices extend its
 * after-hours story." That REVERSES the earlier asset pack, whose guidelines/README.md
 * made the sliced wordmark the logo and this composition a secondary device. The board
 * wins. The sliced wordmark is retired as the identity.
 */

/** Tight art bounds. Nothing bleeds outside this box. */
export const VIEWBOX = { w: 1512, h: 943 };

/** Where the ground is. The sun sets ON it and the building stands ON it. */
export const BASELINE = 685;

/**
 * The sun. A true semicircle, not an arc-with-a-flat-bottom: it is a full circle
 * centred on the baseline, and the baseline is what cuts it. Drawn FIRST so the
 * building occludes it — that overlap is the whole image, dusk behind the workplace.
 */
export const SUN = { cx: 756, cy: BASELINE, r: 685 };
export const SUN_PATH = `M${SUN.cx - SUN.r} ${SUN.cy}A${SUN.r} ${SUN.r} 0 0 1 ${SUN.cx + SUN.r} ${SUN.cy}Z`;

/**
 * The building. Three stepped masses, all landing on the baseline. Deliberately generic
 * — this is any building on the route, not a portrait of one client's office.
 * Order matters only for readability; they are one colour and touch edge-to-edge.
 */
export const MASSES = [
  { x: 621, y: 271, w: 288, h: 414 }, // left shoulder, mid height
  { x: 909, y: 117, w: 414, h: 568 }, // the tall face — carries the windows
  { x: 1323, y: 392, w: 162, h: 293 }, // right shoulder, lowest
];

/**
 * Windows: 4 columns x 3 rows on the tall face. Pitch is 90 across and ~103.5 down,
 * so the grid is slightly taller than square — that is what was delivered, kept as-is.
 */
export const WINDOW = { w: 54, h: 63 };
export const WINDOW_COLS = [990, 1080, 1170, 1260];
export const WINDOW_ROWS = [212, 316, 419];

/** The one light still on. Bottom row, third column. The entire brand in one rectangle. */
export const LIT = { col: 2, row: 2 };

/** Every window as a flat list, each flagged lit or dark. */
export const WINDOWS = WINDOW_ROWS.flatMap((y, row) =>
  WINDOW_COLS.map((x, col) => ({ x, y, ...WINDOW, lit: row === LIT.row && col === LIT.col })),
);

/**
 * The horizon. Three rules receding to the right, each starting further in — the floor
 * running away from you down a corridor. All three share a right edge; the stagger is
 * entirely on the left. This is the device that makes the mark read as a PLACE.
 */
export const BARS = [
  { x: 0, y: 705, w: 1512, h: 40 },
  { x: 153, y: 804, w: 1359, h: 40 },
  { x: 387, y: 903, w: 1125, h: 40 },
];

/**
 * SMALL-SIZE VARIANT — the two-density rule, applied to this mark.
 * At 16px the 12-window grid is 0.5px per window and smears into a grey blur (the exact
 * failure the nine-slat mark had). Below 48px we drop to ONE window — the lit one, scaled
 * up — and two horizon bars. Same silhouette, same story, resolvable pixels.
 */
export const SMALL = {
  // The lit window, grown ~3x but NOT ballooned to fill the face: at 333x267 it read as
  // a lit screen or a doorway rather than a window, and the building stopped being a
  // building. This keeps it in the same place the real one sits — lower right of the
  // tall face — at the smallest size that still resolves at 16px.
  window: { x: 1120, y: 396, w: 163, h: 152 },
  // Chunkier, and only two of them. At 16px a 40-unit bar is a third of a pixel and
  // renders as a grey smear; 78 units survives the downsample as an actual line.
  bars: [
    { x: 0, y: 700, w: 1512, h: 78 },
    { x: 387, y: 865, w: 1125, h: 78 },
  ],
};

/**
 * SQUARE ICON CROP — for tiles, not for layout.
 *
 * The mark is 1.6:1. Fitted into a square favicon it spans the width and leaves 40% of
 * the tile empty top and bottom, which at 16px shrinks the building to a 3px smudge
 * (rendered and looked at, not assumed). So icons crop rather than shrink: keep the
 * dome's peak and right flank, the whole stepped building, and the rules running out
 * from under it. Everything that makes the mark recognisable, none of the empty sky.
 *
 * This is a CROP of the same artwork, never a redrawing of it — the coordinates below
 * are a window onto the geometry above, so the icon can never diverge from the logo.
 */
export const SQUARE = { x: 470, y: -30, w: 1042, h: 1042 };
